import { useState } from "react";
import { UserModel } from "../../../interfaces/models/userModel";
import { ListingInterface } from "../viewListing";
import { Link } from "react-router-dom";
import { isHotel } from "../../../utils/listingUtils";
import {
    ViewListingOwnerSectionWrapper,
    ViewListingOwnerSectionImage,
    ViewListingOwnerSectionTitleWrapper,
    ViewListingOwnerSectionTitle,
    ViewListingOwnerSectionSubtitle,
    ViewListingOwnerSectionQuestionButton,
    ViewListingOwnerSectionText,
    ViewListingHeaderProfileImageWrapper,
    ViewListingHeaderProfileImageVerified,
    ReadMoreButton,
    ViewListingOwnerSectionTextTitle,
    ViewListingOwnerSectionProfileWrapper,
    ViewListingOwnerTextAvatar,
    ViewListingOwnerSectionDetailText,
    ViewListingOwnerSectionLeft,
    ViewListingOwnerSectionRight,
    ViewListingFAQItem,
    ViewListingFAQQuestion,
    ViewListingFAQAnswer,
    ViewListingFAQColumn,
    ViewListingFAQColumnHotel,
    ViewListingFAQHeader,
    ViewListingFAQContent,
    ViewListingFAQIcon,
} from "../viewListingStyling";


interface ExtendedListingInterface extends ListingInterface {
    straID?: string;
}

interface ViewListingOwnerSectionProps {
    listingOwnerProfilePictureURL: string;
    listingOwnerData: UserModel;
    listing: ExtendedListingInterface;
    dates: {
        startDate: string | Date;
        endDate: string | Date;
    };
    toggleSendMessageRequest: () => Promise<void>;
    toggleSendAMessageBtnClicked: () => Promise<void>;
    toggleCalendar: () => void;
    sendAMessageBtnClicked: boolean;
}

export default function ViewListingOwnerSection({
    listingOwnerProfilePictureURL,
    listingOwnerData,
    listing,
    dates,
    toggleSendMessageRequest,
    toggleSendAMessageBtnClicked,
    toggleCalendar,
    sendAMessageBtnClicked,
}: ViewListingOwnerSectionProps) {

    const [isExpanded, setIsExpanded] = useState(true);
    const [expandedFAQs, setExpandedFAQs] = useState<{ [key: string]: boolean }>({});

    return (
        <ViewListingOwnerSectionWrapper>
            {!isHotel(listing.id) && (
                <ViewListingOwnerSectionLeft>
                    <ViewListingOwnerSectionProfileWrapper>
                        <Link to={`/host/profile/${listingOwnerData.id}`} style={{ textDecoration: 'none' }}>
                            {listingOwnerProfilePictureURL ? (
                                <ViewListingHeaderProfileImageWrapper>
                                    <ViewListingOwnerSectionImage src={listingOwnerProfilePictureURL} alt="Listing owner profile picture" />
                                    <ViewListingHeaderProfileImageVerified src={"https://d292awxalydr86.cloudfront.net/Universal+icons/Vector.png"} alt="Verified tick" />
                                </ViewListingHeaderProfileImageWrapper>
                            ) : (
                                <ViewListingHeaderProfileImageWrapper>
                                    <ViewListingOwnerTextAvatar style={{ height: "64px", width: "64px" }}>{listingOwnerData?.firstName?.[0]?.toUpperCase() || '?'}</ViewListingOwnerTextAvatar>
                                    <ViewListingHeaderProfileImageVerified src={"https://d292awxalydr86.cloudfront.net/Universal+icons/Vector.png"} alt="Verified tick"/>
                                </ViewListingHeaderProfileImageWrapper>
                            )}
                        </Link>

                        <ViewListingOwnerSectionTitleWrapper>
                            <ViewListingOwnerSectionTitle>{listingOwnerData?.firstName || 'Host'}</ViewListingOwnerSectionTitle>
                            <ViewListingOwnerSectionSubtitle>
                                {listingOwnerData?.userIDConfirmed ? "Verified host" : (listingOwnerData?.createdAt && (new Date(listingOwnerData.createdAt).getTime() - new Date().getTime()) / (1000 * 3600 * 24) <= 14) ? "New host" : "Host"}
                            </ViewListingOwnerSectionSubtitle>
                        </ViewListingOwnerSectionTitleWrapper>
                    </ViewListingOwnerSectionProfileWrapper>

                    <ViewListingOwnerSectionTextTitle>About your host</ViewListingOwnerSectionTextTitle>

                    <ViewListingOwnerSectionText style={{ marginTop: "16px" }}>
                        {listingOwnerData.profileDescription ? (
                            isExpanded && listingOwnerData.profileDescription.length > 50 ? (
                                listingOwnerData.profileDescription.slice(0, 50) + "..."
                            ) : (
                                listingOwnerData.profileDescription
                            )
                        ) : (
                            <>
                                <span>{listingOwnerData.firstName} hasn't written about them yet.</span>
                                &nbsp;Don't worry, you can still message them to ask questions or see what they're like.
                            </>
                        )}
                        {listingOwnerData.profileDescription?.length > 50 && (
                            <>
                                <br />
                                <ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? "Read more" : "Show less"}</ReadMoreButton>
                            </>
                        )}
                    </ViewListingOwnerSectionText>

                    {listing && listing.straID && (
                        <ViewListingOwnerSectionText style={{ marginTop: "16px" }}>
                            <>
                                <span>STRA registration:</span>
                                &nbsp;{listing.straID}
                            </>
                        </ViewListingOwnerSectionText>
                    )}

                    <ViewListingOwnerSectionDetailText style={{ marginTop: "16px" }}>Speaks English</ViewListingOwnerSectionDetailText>

                    <ViewListingOwnerSectionDetailText style={{ marginTop: "8px" }}>100% response rate</ViewListingOwnerSectionDetailText>

                    <ViewListingOwnerSectionQuestionButton
                        type="button"
                        style={{ marginTop: "24px" }}
                        onClick={() => {
                            if (dates.startDate !== "anyArrival" && dates.endDate !== "anyDeparture") {
                                toggleSendMessageRequest();
                                return;
                            }
                            if (!sendAMessageBtnClicked) {
                                toggleSendAMessageBtnClicked();
                            }
                            toggleCalendar();
                        }}
                    >
                        Message host
                    </ViewListingOwnerSectionQuestionButton>
                </ViewListingOwnerSectionLeft>
            )}

            <ViewListingOwnerSectionRight style={isHotel(listing.id) ? { width: '100%' } : undefined}>
                <ViewListingOwnerSectionTextTitle>Questions you asked</ViewListingOwnerSectionTextTitle>
                
                {isHotel(listing.id) ? (
                    <ViewListingFAQColumnHotel>
                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, checkin: !prev.checkin }))}>
                                <ViewListingFAQQuestion>What are the check-in and parking details?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.checkin} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.checkin}>
                                <ViewListingFAQAnswer>Check-in is from {listing.checkInTime}:00 and check-out is before {listing.checkOutTime}:00. Self check-in is available with a keypad code that will be provided before arrival. Free parking is available on premises.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, payment: !prev.payment }))}>
                                <ViewListingFAQQuestion>What payment methods are accepted?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.payment} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.payment}>
                                <ViewListingFAQAnswer>We accept all major credit cards through our secure payment system. The total amount is split into two payments - 50% deposit to secure your booking and the remaining balance due 30 days before check-in.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, cancel: !prev.cancel }))}>
                                <ViewListingFAQQuestion>What if I need to cancel my booking?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.cancel} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.cancel}>
                                <ViewListingFAQAnswer>This property has a {listing.cancellationPolicy} cancellation policy. You can cancel up to 30 days before check-in for a full refund, minus the service fee. Cancellations within 30 days are subject to our cancellation policy.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, pets: !prev.pets }))}>
                                <ViewListingFAQQuestion>Can I bring my pet?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.pets} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.pets}>
                                <ViewListingFAQAnswer>
                                    {listing.houseRules.includes("pet friendly") 
                                        ? "Yes, well-behaved pets are welcome! Please let us know in advance if you're bringing a pet."
                                        : "Sorry, pets are not allowed at this property."} Service animals are always welcome in accordance with accessibility laws.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, wifi: !prev.wifi }))}>
                                <ViewListingFAQQuestion>Is WiFi available?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.wifi} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.wifi}>
                                <ViewListingFAQAnswer>
                                    Yes, complimentary high-speed WiFi is available throughout the property. The network name and password will be provided upon check-in.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, amenities: !prev.amenities }))}>
                                <ViewListingFAQQuestion>What amenities are included?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.amenities} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.amenities}>
                                <ViewListingFAQAnswer>
                                    This property includes {listing.amenities?.join(', ') || 'standard amenities'}. All amenities are available for guest use during your stay. Please check the listing details for a complete list of available facilities.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>
                    </ViewListingFAQColumnHotel>
                ) : (
                    <ViewListingFAQColumn>
                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, checkin: !prev.checkin }))}>
                                <ViewListingFAQQuestion>What are the check-in and parking details?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.checkin} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.checkin}>
                                <ViewListingFAQAnswer>Check-in is from {listing.checkInTime}:00 and check-out is before {listing.checkOutTime}:00. Self check-in is available with a keypad code that will be provided before arrival. Free parking is available on premises.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, payment: !prev.payment }))}>
                                <ViewListingFAQQuestion>What payment methods are accepted?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.payment} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.payment}>
                                <ViewListingFAQAnswer>We accept all major credit cards through our secure payment system. The total amount is split into two payments - 50% deposit to secure your booking and the remaining balance due 30 days before check-in.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, cancel: !prev.cancel }))}>
                                <ViewListingFAQQuestion>What if I need to cancel my booking?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.cancel} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.cancel}>
                                <ViewListingFAQAnswer>This property has a {listing.cancellationPolicy} cancellation policy. You can cancel up to 30 days before check-in for a full refund, minus the service fee. Cancellations within 30 days are subject to our cancellation policy.</ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, pets: !prev.pets }))}>
                                <ViewListingFAQQuestion>Can I bring my pet?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.pets} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.pets}>
                                <ViewListingFAQAnswer>
                                    {listing.houseRules.includes("pet friendly") 
                                        ? "Yes, well-behaved pets are welcome! Please let us know in advance if you're bringing a pet."
                                        : "Sorry, pets are not allowed at this property."} Service animals are always welcome in accordance with accessibility laws.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, wifi: !prev.wifi }))}>
                                <ViewListingFAQQuestion>Is WiFi available?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.wifi} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.wifi}>
                                <ViewListingFAQAnswer>
                                    Yes, complimentary high-speed WiFi is available throughout the property. The network name and password will be provided upon check-in.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>

                        <ViewListingFAQItem>
                            <ViewListingFAQHeader onClick={() => setExpandedFAQs(prev => ({ ...prev, amenities: !prev.amenities }))}>
                                <ViewListingFAQQuestion>What amenities are included?</ViewListingFAQQuestion>
                                <ViewListingFAQIcon 
                                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" 
                                    alt="Expand" 
                                    isExpanded={!!expandedFAQs.amenities} 
                                />
                            </ViewListingFAQHeader>
                            <ViewListingFAQContent isExpanded={!!expandedFAQs.amenities}>
                                <ViewListingFAQAnswer>
                                    This property includes {listing.amenities?.join(', ') || 'standard amenities'}. All amenities are available for guest use during your stay. Please check the listing details for a complete list of available facilities.
                                </ViewListingFAQAnswer>
                            </ViewListingFAQContent>
                        </ViewListingFAQItem>
                    </ViewListingFAQColumn>
                )}
            </ViewListingOwnerSectionRight>
        </ViewListingOwnerSectionWrapper>
    );
}
